import { makeAutoObservable } from "mobx";
import firebase from "firebase/compat/app";
import { WhatsappFlow } from "../models";
import bots from "./bots";
import { handleMetaWhatsappFlow } from "../services/chathub";

class WhatsappFlowsStore {
  private db: firebase.firestore.Firestore;

  flows: WhatsappFlow[] = [];

  constructor() {
    makeAutoObservable(this);

    this.db = firebase.firestore();
  }

  loadFlows = async () => {
    if (!bots.selectedBot) return;
    this.setFlows([]);

    let query = this.db
      .collection("bots")
      .doc(String(bots.selectedBot.id))
      .collection("whatsappFlows")
      .orderBy("createdAt", "desc");

    const snapshot = await query.get();

    const data = snapshot.docs
      .map((doc) => {
        const obj = doc.data();
        obj.id = doc.id;
        return obj;
      })
      .filter((f: any) => !f.deletedAt) as WhatsappFlow[];

    console.log(snapshot);
    this.setFlows(data);

    return data;
  };

  updateOrCreateFlow = async (
    flow: WhatsappFlow,
    genericFlow: WhatsappFlow,
    status: "draft" | "published"
  ) => {
    if (!bots.selectedBot) return;

    if (flow.type === "customized") {
      if (!flow.id) {
        const doc = this.db
          .collection("bots")
          .doc(String(bots.selectedBot.id))
          .collection("whatsappFlows")
          .doc();

        flow.id = doc.id;
        await doc.set({
          ...genericFlow,
          type: "customized",
          metaId: flow.metaId,
          status: "published",
          createdAt: firebase.firestore.FieldValue.serverTimestamp(),
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
      } else {
        const doc = this.db
          .collection("bots")
          .doc(String(bots.selectedBot.id))
          .collection("whatsappFlows")
          .doc(flow.id);

        await doc.update({
          name: flow.name,
          metaId: flow.metaId,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        });
      }
      return;
    }

    if (!flow.id) {
      const doc = this.db
        .collection("bots")
        .doc(String(bots.selectedBot.id))
        .collection("whatsappFlows")
        .doc();

      flow.id = doc.id;
      const data = await handleMetaWhatsappFlow(
        bots.selectedBot.id,
        bots.selectedBot.key,
        bots.selectedBot.password,
        {
          name: flow.name,
          categories: flow.categories,
          flow: flow.flow,
          publish: status === "published",
        }
      );
      if (!data?.data?.id) {
        throw new Error("Flow id not found");
      }
      await doc.set({
        ...genericFlow,
        metaId: data.data.id,
        status,
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
    } else {
      if (!flow.metaId) {
        throw new Error("Flow id not found");
      }
      const doc = this.db
        .collection("bots")
        .doc(String(bots.selectedBot.id))
        .collection("whatsappFlows")
        .doc(flow.id);

      await handleMetaWhatsappFlow(
        bots.selectedBot.id,
        bots.selectedBot.key,
        bots.selectedBot.password,
        {
          metaId: flow.metaId,
          name: flow.name,
          categories: flow.categories,
          flow: flow.flow,
          publish: status === "published",
        }
      );
      await doc.set({
        ...genericFlow,
        status,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
    }
  };

  loadFlow = async (id: string) => {
    if (!bots.selectedBot) return;

    const doc = this.db
      .collection("bots")
      .doc(String(bots.selectedBot.id))
      .collection("whatsappFlows")
      .doc(id);

    const snap = await doc.get();
    const snapData = snap.data() as WhatsappFlow;

    if (!snapData || (!snapData.metaId && snapData.type !== "customized")) {
      throw new Error("Flow not found");
    }

    snapData.id = snap.id;

    return snapData;
  };

  cloneFlow = async (flow: WhatsappFlow) => {
    if (!bots.selectedBot) return;

    const doc = this.db
      .collection("bots")
      .doc(String(bots.selectedBot.id))
      .collection("whatsappFlows")
      .doc();

    flow.id = doc.id;
    await doc.set({
      ...flow,
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });

    await this.loadFlows();
  };

  deprecateFlow = async (flow: WhatsappFlow) => {
    if (!bots.selectedBot) return;

    const doc = this.db
      .collection("bots")
      .doc(String(bots.selectedBot.id))
      .collection("whatsappFlows")
      .doc(flow.id);

    await doc.update({
      status: "deprecated",
      deprecatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });

    if (flow.type !== "customized") {
      const data = await handleMetaWhatsappFlow(
        bots.selectedBot.id,
        bots.selectedBot.key,
        bots.selectedBot.password,
        {
          metaId: flow.metaId,
          deprecated: true,
        }
      );

      if (!data?.success) {
        throw new Error(data?.message);
      }
    }

    await this.loadFlows();
  };

  setFlows = (flows: WhatsappFlow[]) => {
    this.flows = flows;
  };
}

export default new WhatsappFlowsStore();
